import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Flex, HelpIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useFeatureFlag } from "@jsq/flags-react";
import { client as apolloClient } from "~/apollo/client";
import { useAppContext } from "~/contexts/appContext";
import { useSearchFundFamiliesQuery } from "~/graphql/supergraphHooks";
import { hasBillingFARole, hasBillingGPRole } from "~/utils/userRoles";
import { AssignBillingGroupModal } from "../AssignBillingGroupModal";
import {
  getSelectionType,
  getEntitySelectionCount,
  getGroupSelectionCount,
  filterNotAssignedGroup,
  isNotAssignedGroupNode,
} from "../bulkActionsUtils";
import {
  ENTITY_HEADING,
  ENTITY_TOOLTIP,
  BILLING_GROUP_HEADING,
  BILLING_GROUP_TOOLTIP,
  EDIT_ENTITY_DETAILS_HEADING,
  EDIT_ENTITY_DETAILS_TOOLTIP,
  NOT_ASSIGNED_RAW_ID,
  NOT_ASSIGNED_LABEL,
  OVERRIDE_EUM_VALUE_LABEL,
  EDIT_EUM_CALCULATION_VALUES_LABEL,
  EUM_MULTI_SELECT_DISABLED_TOOLTIP,
  ROLE_DISABLED_TOOLTIP,
  MISSING_GROUP_TOOLTIP,
} from "../constants";
import { EditEumCalculationValuesModal } from "../EditEumCalculationValuesModal";
import { EditFirstCapitalCallDateModal } from "../EditFirstCapitalCallDateModal";
import { MarkAsStandaloneModal } from "../MarkAsStandaloneModal";
import { OverrideEumValueModal } from "../OverrideEumValueModal";
import { ReassignBillingGroupModal } from "../ReassignBillingGroupModal";
import { RemoveBillingGroupModal } from "../RemoveBillingGroupModal";
import { RemoveFromBillingGroupModal } from "../RemoveFromBillingGroupModal";
import { RenameBillingGroupModal } from "../RenameBillingGroupModal";
import { EntitySelectionType, CaseTypeDisplay } from "../types";
import {
  bulkActionsPanel,
  bulkActionsPanelHeader,
  actionsSection,
  actionItem,
  actionItemDisabled,
  divider,
  sectionHeading,
} from "./styles_vanilla.css";
import type { BulkActionsPanelProps, EntityDetailsActionsProps } from "./types";
import type { AssignBillingGroupFormValues } from "../AssignBillingGroupModal/types";
import type { EditEumCalculationValuesSubmitData } from "../EditEumCalculationValuesModal/types";
import type { OverrideEumValueSubmitData } from "../OverrideEumValueModal/types";
import type { ReassignBillingGroupFormValues } from "../ReassignBillingGroupModal/types";
import type { EntityRowNode } from "../types";

type ActionSectionHeadingProps = {
  heading: string;
  tooltip: string;
};

const ActionSectionHeading = ({ heading, tooltip }: ActionSectionHeadingProps) => (
  <Flex alignItems="center" justifyContent="space-between" className={sectionHeading}>
    <Text variant="secondary-sm" color={Colors.Neutral600}>
      {heading}
    </Text>
    <HelpIndicator id={`${heading}-tooltip`} placement="top" content={tooltip}>
      <Icon type="question-circle" color={Colors.Neutral600} variant="outline" />
    </HelpIndicator>
  </Flex>
);

type ActionItemProps = {
  label: string;
  tooltipId: string;
  onClick: () => void;
  disabledTooltip?: string | null;
};

const ActionItem = ({ label, tooltipId, onClick, disabledTooltip }: ActionItemProps) =>
  disabledTooltip ? (
    <HelpIndicator id={tooltipId} placement="top" content={disabledTooltip}>
      <Text variant="regular-md" color={Colors.Neutral400} className={actionItemDisabled}>
        {label}
      </Text>
    </HelpIndicator>
  ) : (
    <Text
      variant="regular-md"
      color={Colors.Neutral900}
      className={actionItem}
      onClick={onClick}
    >
      {label}
    </Text>
  );

type UnassignedEntityActionsProps = {
  onAssignClick: () => void;
  onMarkStandaloneClick: () => void;
  roleDisabledTooltip: string | null;
};

const UnassignedEntityActions = ({
  onAssignClick,
  onMarkStandaloneClick,
  roleDisabledTooltip,
}: UnassignedEntityActionsProps) => (
  <Flex flexDirection="column" className={actionsSection}>
    <ActionSectionHeading heading={ENTITY_HEADING} tooltip={ENTITY_TOOLTIP} />
    <ActionItem
      label="Assign to billing group"
      tooltipId="assign-billing-group-disabled-tooltip"
      onClick={onAssignClick}
      disabledTooltip={roleDisabledTooltip}
    />
    <ActionItem
      label="Mark as a standalone entity"
      tooltipId="mark-standalone-disabled-tooltip"
      onClick={onMarkStandaloneClick}
      disabledTooltip={roleDisabledTooltip}
    />
  </Flex>
);

type AssignedEntityActionsProps = {
  onReassignClick: () => void;
  onRemoveClick: () => void;
  onMarkStandaloneClick: () => void;
  isRemoveDisabled: boolean;
  roleDisabledTooltip: string | null;
};

const AssignedEntityActions = ({
  onReassignClick,
  onRemoveClick,
  onMarkStandaloneClick,
  isRemoveDisabled,
  roleDisabledTooltip,
}: AssignedEntityActionsProps) => (
  <Flex flexDirection="column" className={actionsSection}>
    <ActionSectionHeading heading={ENTITY_HEADING} tooltip={ENTITY_TOOLTIP} />
    <ActionItem
      label="Re-assign to billing group"
      tooltipId="reassign-billing-group-disabled-tooltip"
      onClick={onReassignClick}
      disabledTooltip={roleDisabledTooltip}
    />
    <ActionItem
      label="Mark as a standalone entity"
      tooltipId="mark-standalone-disabled-tooltip"
      onClick={onMarkStandaloneClick}
      disabledTooltip={roleDisabledTooltip}
    />
    <ActionItem
      label="Remove from billing group"
      tooltipId="remove-from-billing-group-disabled-tooltip"
      onClick={onRemoveClick}
      disabledTooltip={roleDisabledTooltip ?? (isRemoveDisabled ? MISSING_GROUP_TOOLTIP : null)}
    />
  </Flex>
);

const EntityDetailsActions = ({
  onEditFirstCapitalCallDateClick,
  onOverrideEumValueClick,
  onEditEumCalculationValuesClick,
  showFirstCapitalCallDate,
  showEumOverride,
  isEumActionDisabled,
  faRoleDisabledTooltip,
  eumRoleDisabledTooltip,
}: EntityDetailsActionsProps) => {
  const eumDisabledTooltip =
    eumRoleDisabledTooltip ?? (isEumActionDisabled ? EUM_MULTI_SELECT_DISABLED_TOOLTIP : null);
  return (
    <Flex flexDirection="column" className={actionsSection}>
      <ActionSectionHeading
        heading={EDIT_ENTITY_DETAILS_HEADING}
        tooltip={EDIT_ENTITY_DETAILS_TOOLTIP}
      />
      {showFirstCapitalCallDate && (
        <ActionItem
          label={`Edit ${CaseTypeDisplay.FirstCapitalCallDate.toLowerCase()}`}
          tooltipId="edit-first-capital-call-date-disabled-tooltip"
          onClick={onEditFirstCapitalCallDateClick}
          disabledTooltip={faRoleDisabledTooltip}
        />
      )}
      {showEumOverride && (
        <>
          <ActionItem
            label={OVERRIDE_EUM_VALUE_LABEL}
            tooltipId="override-eum-value-disabled-tooltip"
            onClick={onOverrideEumValueClick}
            disabledTooltip={eumDisabledTooltip}
          />
          <ActionItem
            label={EDIT_EUM_CALCULATION_VALUES_LABEL}
            tooltipId="edit-eum-calculation-values-disabled-tooltip"
            onClick={onEditEumCalculationValuesClick}
            disabledTooltip={eumDisabledTooltip}
          />
        </>
      )}
    </Flex>
  );
};

type BillingGroupActionsProps = {
  onRenameClick: () => void;
  onRemoveGroupClick: () => void;
  isRenameDisabled: boolean;
  isRemoveGroupDisabled: boolean;
  roleDisabledTooltip: string | null;
};

const BillingGroupActions = ({
  onRenameClick,
  onRemoveGroupClick,
  isRenameDisabled,
  isRemoveGroupDisabled,
  roleDisabledTooltip,
}: BillingGroupActionsProps) => (
  <Flex flexDirection="column" className={actionsSection}>
    <ActionSectionHeading heading={BILLING_GROUP_HEADING} tooltip={BILLING_GROUP_TOOLTIP} />
    <ActionItem
      label="Rename billing group"
      tooltipId="rename-billing-group-disabled-tooltip"
      onClick={onRenameClick}
      disabledTooltip={roleDisabledTooltip ?? (isRenameDisabled ? MISSING_GROUP_TOOLTIP : null)}
    />
    <ActionItem
      label="Remove billing group"
      tooltipId="remove-billing-group-disabled-tooltip"
      onClick={onRemoveGroupClick}
      disabledTooltip={
        roleDisabledTooltip ?? (isRemoveGroupDisabled ? MISSING_GROUP_TOOLTIP : null)
      }
    />
  </Flex>
);

const pluralize = (count: number, singular: string, plural: string): string => {
  if (count === 1) {
    return `1 ${singular} selected`;
  }
  return `${count} ${plural} selected`;
};

const getSelectionLabel = (
  selectionType: EntitySelectionType,
  entityCount: number,
  groupCount: number,
): string => {
  if (selectionType === EntitySelectionType.BillingGroup) {
    return pluralize(groupCount, "billing group", "billing groups");
  }
  return pluralize(entityCount, "entity", "entities");
};

const getBillingGroupNameFromGrid = (
  api: BulkActionsPanelProps["api"],
  billingGroupId: string | null,
): string => {
  if (!billingGroupId || billingGroupId === NOT_ASSIGNED_RAW_ID) {
    return NOT_ASSIGNED_LABEL;
  }

  const groupNameLookup = new Map<string, string>();

  // Build a lookup map of billing group IDs to names by iterating through all nodes
  if (api.forEachNode) {
    api.forEachNode((node) => {
      if (!node.data) {
        return;
      }
      if (node.data.type === "billingGroup" && node.data.billingGroupName) {
        groupNameLookup.set(node.data.billingGroupId, node.data.billingGroupName);
      }
    });
  }

  return groupNameLookup.get(billingGroupId) ?? "";
};

export const BulkActionsPanel = ({
  api,
  clientId,
  onProposedChangeSubmit,
  onEditFirstCapitalCallDateSubmit,
}: BulkActionsPanelProps) => {
  const [selectedNodes, setSelectedNodes] = useState<EntityRowNode[]>([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isStandaloneModalOpen, setIsStandaloneModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isRemoveGroupModalOpen, setIsRemoveGroupModalOpen] = useState(false);
  const [isEditCapitalCallDateModalOpen, setIsEditCapitalCallDateModalOpen] = useState(false);
  const [isOverrideEumValueModalOpen, setIsOverrideEumValueModalOpen] = useState(false);
  const [isEditEumCalculationValuesModalOpen, setIsEditEumCalculationValuesModalOpen] =
    useState(false);
  const [notAssignedGroupSelected, setNotAssignedGroupSelected] = useState(false);
  const isBulkActionsEnabled = useFeatureFlag("billingFAManualTaggingBulkActions", false);
  const isFCCEnabled = useFeatureFlag("billingFirstCapitalCallDate", false);
  const isEumOverrideEnabled = useFeatureFlag("showCanopyEumOverride", false);
  const { user } = useAppContext();

  // Inclusion predicates matching what the target mutations accept: dual-role
  // (FA + GP) users correctly see both action groups enabled (ISE-856).
  const userRoles = user?.roles ?? [];
  const faRoleDisabledTooltip = hasBillingFARole(userRoles) ? null : ROLE_DISABLED_TOOLTIP;
  const eumRoleDisabledTooltip = hasBillingGPRole(userRoles) ? null : ROLE_DISABLED_TOOLTIP;

  // Fetch valid fund families to validate billing group IDs
  const { data: familiesData } = useSearchFundFamiliesQuery({
    variables: { clientId },
    client: apolloClient,
    fetchPolicy: "cache-first",
  });

  const validFamilyIds = useMemo(() => {
    const families = familiesData?.searchFundFamilies?.families ?? [];
    return new Set(families.map((f) => f.id));
  }, [familiesData]);

  const handleSelectionChanged = useCallback(() => {
    const nodes = api.getSelectedNodes();
    const hasNotAssignedGroup = nodes.some((node) =>
      isNotAssignedGroupNode({ group: node.group, data: node.data, key: node.key }),
    );
    setNotAssignedGroupSelected(hasNotAssignedGroup);
    setSelectedNodes(filterNotAssignedGroup(nodes));
  }, [api]);

  useEffect(() => {
    api.addEventListener("selectionChanged", handleSelectionChanged);
    return () => {
      api.removeEventListener("selectionChanged", handleSelectionChanged);
    };
  }, [api, handleSelectionChanged]);

  const clearSelection = () => {
    api.deselectAll();
  };

  const onAssignClick = () => {
    setIsAssignModalOpen(true);
  };

  const onAssignClose = () => {
    setIsAssignModalOpen(false);
  };

  const onReassignClick = () => {
    setIsReassignModalOpen(true);
  };

  const onReassignClose = () => {
    setIsReassignModalOpen(false);
  };

  // For group nodes, the billingGroupId might be in the key property
  const firstNode = selectedNodes[0];
  const selectedBillingGroupId =
    firstNode?.data?.billingGroupId ?? (firstNode?.group ? (firstNode.key ?? null) : null);
  const selectedBillingGroupName = getBillingGroupNameFromGrid(api, selectedBillingGroupId);

  // Check if the selected billing group exists in the billing backend
  // Disable remove/rename actions if the billing group doesn't exist in the backend (data sync issue)
  // Note: Unassigned entities use UnassignedEntityActions component, so they never see the remove action
  const isBillingGroupMissingInBackend =
    selectedBillingGroupId !== null && !validFamilyIds.has(selectedBillingGroupId);
  const isBillingGroupNameEmpty = !selectedBillingGroupName;
  const isRemoveDisabled = isBillingGroupMissingInBackend || isBillingGroupNameEmpty;
  const isRenameDisabled = isBillingGroupMissingInBackend || isBillingGroupNameEmpty;

  const getSelectedEntityData = useCallback(() => {
    const entityIds: number[] = [];
    const entityNames: string[] = [];
    for (const node of selectedNodes) {
      const id = node.data?.entityId;
      const name = node.data?.entity;
      if (!node.group && typeof id === "number") {
        entityIds.push(id);
        if (typeof name === "string") {
          entityNames.push(name);
        }
      }
    }
    return { entityIds, entityNames };
  }, [selectedNodes]);

  const onEditCapitalCallDateClick = () => {
    setIsEditCapitalCallDateModalOpen(true);
  };

  const onEditCapitalCallDateClose = () => {
    setIsEditCapitalCallDateModalOpen(false);
  };

  const onOverrideEumValueClick = () => {
    setIsOverrideEumValueModalOpen(true);
  };

  const onOverrideEumValueClose = () => {
    setIsOverrideEumValueModalOpen(false);
  };

  const onEditEumCalculationValuesClick = () => {
    setIsEditEumCalculationValuesModalOpen(true);
  };

  const onEditEumCalculationValuesClose = () => {
    setIsEditEumCalculationValuesModalOpen(false);
  };

  const onOverrideEumValueSubmit = useCallback(
    async (data: OverrideEumValueSubmitData) => {
      const { entityIds, entityNames } = getSelectedEntityData();
      if (entityIds.length > 0) {
        const currentEumValue = selectedNodes[0]?.data?.softwareEumUsd ?? null;
        await onProposedChangeSubmit({
          action: "eum_value_override",
          entityIds,
          entityNames,
          currentEumValue,
          proposedEumValue: data.newEumValue,
          reasonCode: data.reasonCode,
          description: data.description,
          file: data.file,
        });
        setIsOverrideEumValueModalOpen(false);
        api.deselectAll();
      }
    },
    [getSelectedEntityData, onProposedChangeSubmit, api, selectedNodes],
  );

  const onEditEumCalculationValuesSubmit = useCallback(
    async (data: EditEumCalculationValuesSubmitData) => {
      const { entityIds, entityNames } = getSelectedEntityData();
      if (entityIds.length > 0) {
        const currentEumValue = selectedNodes[0]?.data?.softwareEumUsd ?? null;
        const current = {
          eumValue: currentEumValue,
          netCapitalValue: null,
          totalCommitmentValue: null,
          distributionsValue: null,
        };
        await onProposedChangeSubmit({
          action: "eum_calc_edit",
          entityIds,
          entityNames,
          current,
          proposed: {
            eumValue: currentEumValue,
            netCapitalValue: data.newNetCapitalValue,
            totalCommitmentValue: data.newTotalCommitmentValue,
            distributionsValue: data.newDistributionsValue,
          },
          reasonCode: data.reasonCode,
          description: data.description,
          file: data.file,
        });
        setIsEditEumCalculationValuesModalOpen(false);
        api.deselectAll();
      }
    },
    [getSelectedEntityData, onProposedChangeSubmit, api, selectedNodes],
  );

  const onEditCapitalCallDateSubmit = useCallback(
    (date: Date) => {
      const { entityIds, entityNames } = getSelectedEntityData();
      if (entityIds.length > 0) {
        onEditFirstCapitalCallDateSubmit(entityIds, entityNames, date);
        setIsEditCapitalCallDateModalOpen(false);
        api.deselectAll();
      }
    },
    [getSelectedEntityData, onEditFirstCapitalCallDateSubmit, api],
  );

  const onRemoveClick = () => {
    setIsRemoveModalOpen(true);
  };

  const onRemoveClose = () => {
    setIsRemoveModalOpen(false);
  };

  const onRemoveSubmit = useCallback(async () => {
    const { entityIds, entityNames } = getSelectedEntityData();
    if (entityIds.length > 0 && selectedBillingGroupId) {
      await onProposedChangeSubmit({
        action: "remove",
        entityIds,
        entityNames,
        billingGroupId: selectedBillingGroupId,
        billingGroupName: selectedBillingGroupName,
      });
      setIsRemoveModalOpen(false);
      api.deselectAll();
    }
  }, [
    getSelectedEntityData,
    selectedBillingGroupId,
    selectedBillingGroupName,
    onProposedChangeSubmit,
    api,
  ]);

  const onMarkStandaloneClick = () => {
    setIsStandaloneModalOpen(true);
  };

  const onStandaloneClose = () => {
    setIsStandaloneModalOpen(false);
  };

  const onStandaloneSubmit = useCallback(async () => {
    const { entityIds, entityNames } = getSelectedEntityData();
    if (entityIds.length > 0) {
      await onProposedChangeSubmit({
        action: "standalone",
        entityIds,
        entityNames,
      });
      setIsStandaloneModalOpen(false);
      api.deselectAll();
    }
  }, [getSelectedEntityData, onProposedChangeSubmit, api]);

  const onRenameClick = () => {
    setIsRenameModalOpen(true);
  };

  const onRenameClose = () => {
    setIsRenameModalOpen(false);
  };

  const onRenameSubmit = useCallback(
    async (newName: string) => {
      if (selectedBillingGroupId) {
        await onProposedChangeSubmit({
          action: "rename",
          billingGroupId: selectedBillingGroupId,
          billingGroupName: selectedBillingGroupName,
          newBillingGroupName: newName,
        });
        setIsRenameModalOpen(false);
        api.deselectAll();
      }
    },
    [selectedBillingGroupId, selectedBillingGroupName, onProposedChangeSubmit, api],
  );

  const onRemoveGroupClick = () => {
    setIsRemoveGroupModalOpen(true);
  };

  const onRemoveGroupClose = () => {
    setIsRemoveGroupModalOpen(false);
  };

  const onRemoveGroupSubmit = useCallback(async () => {
    if (selectedBillingGroupId) {
      const entityNames: string[] = [];
      api.forEachNode((node) => {
        if (
          !node.group &&
          node.data?.billingGroupId === selectedBillingGroupId &&
          node.data?.entity
        ) {
          entityNames.push(node.data.entity);
        }
      });
      await onProposedChangeSubmit({
        action: "remove_group",
        billingGroupId: selectedBillingGroupId,
        billingGroupName: selectedBillingGroupName,
        entityNames,
      });
      setIsRemoveGroupModalOpen(false);
      api.deselectAll();
    }
  }, [selectedBillingGroupId, selectedBillingGroupName, onProposedChangeSubmit, api]);

  const onAssignSubmit = useCallback(
    async (formValues: AssignBillingGroupFormValues, familyDisplayName: string) => {
      const { entityIds, entityNames } = getSelectedEntityData();
      if (entityIds.length > 0) {
        await onProposedChangeSubmit({ entityIds, entityNames, formValues, familyDisplayName });
        setIsAssignModalOpen(false);
        api.deselectAll();
      }
    },
    [getSelectedEntityData, onProposedChangeSubmit, api],
  );

  const onReassignSubmit = useCallback(
    async (formValues: ReassignBillingGroupFormValues, familyDisplayName: string) => {
      const { entityIds, entityNames } = getSelectedEntityData();
      if (entityIds.length > 0) {
        await onProposedChangeSubmit({ entityIds, entityNames, formValues, familyDisplayName });
        setIsReassignModalOpen(false);
        api.deselectAll();
      }
    },
    [getSelectedEntityData, onProposedChangeSubmit, api],
  );

  const baseSelectionType = getSelectionType(selectedNodes);
  const selectionType =
    baseSelectionType === EntitySelectionType.None && notAssignedGroupSelected
      ? EntitySelectionType.UnassignedEntity
      : baseSelectionType;
  const entityCount = getEntitySelectionCount(selectedNodes);
  const groupCount = getGroupSelectionCount(selectedNodes);
  const label = getSelectionLabel(selectionType, entityCount, groupCount);
  const selectedEntityEumValue = selectedNodes[0]?.data?.softwareEumUsd ?? null;
  const selectedEntityCalculationValues = {
    eumValue: selectedEntityEumValue,
    netCapitalValue: null,
    totalCommitmentValue: null,
    distributionsValue: null,
  };

  return (
    <>
      <Flex
        flexDirection="column"
        className={bulkActionsPanel}
        data-testid="bulk-actions-panel"
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          className={bulkActionsPanelHeader}
        >
          <Text variant="regular-md" data-testid="selected-count">
            {label}
          </Text>
          {selectedNodes.length > 0 && (
            <Button variant="link" onClick={clearSelection}>
              Clear selection
            </Button>
          )}
        </Flex>
        <div className={divider} />
        {selectionType === EntitySelectionType.UnassignedEntity && (
          <UnassignedEntityActions
            onAssignClick={onAssignClick}
            onMarkStandaloneClick={onMarkStandaloneClick}
            roleDisabledTooltip={faRoleDisabledTooltip}
          />
        )}
        {selectionType === EntitySelectionType.AssignedEntity && (
          <AssignedEntityActions
            onReassignClick={onReassignClick}
            onRemoveClick={onRemoveClick}
            onMarkStandaloneClick={onMarkStandaloneClick}
            isRemoveDisabled={isRemoveDisabled}
            roleDisabledTooltip={faRoleDisabledTooltip}
          />
        )}
        {selectionType === EntitySelectionType.BillingGroup && (
          <BillingGroupActions
            onRenameClick={onRenameClick}
            onRemoveGroupClick={onRemoveGroupClick}
            isRenameDisabled={isRenameDisabled}
            isRemoveGroupDisabled={isBillingGroupMissingInBackend}
            roleDisabledTooltip={faRoleDisabledTooltip}
          />
        )}
        {(isFCCEnabled || isEumOverrideEnabled) &&
          (selectionType === EntitySelectionType.UnassignedEntity ||
            selectionType === EntitySelectionType.AssignedEntity) && (
            <EntityDetailsActions
              onEditFirstCapitalCallDateClick={onEditCapitalCallDateClick}
              onOverrideEumValueClick={onOverrideEumValueClick}
              onEditEumCalculationValuesClick={onEditEumCalculationValuesClick}
              showFirstCapitalCallDate={isFCCEnabled}
              showEumOverride={isEumOverrideEnabled}
              isEumActionDisabled={entityCount > 1}
              faRoleDisabledTooltip={faRoleDisabledTooltip}
              eumRoleDisabledTooltip={eumRoleDisabledTooltip}
            />
          )}
        {selectionType !== EntitySelectionType.None && <div className={divider} />}
      </Flex>
      <AssignBillingGroupModal
        isOpen={isAssignModalOpen}
        onClose={onAssignClose}
        onSubmit={onAssignSubmit}
        selectedEntityCount={entityCount}
        clientId={clientId}
      />
      {isBulkActionsEnabled && (
        <ReassignBillingGroupModal
          isOpen={isReassignModalOpen}
          onClose={onReassignClose}
          onSubmit={onReassignSubmit}
          selectedEntityCount={entityCount}
          clientId={clientId}
        />
      )}
      {isBulkActionsEnabled && (
        <RemoveFromBillingGroupModal
          isOpen={isRemoveModalOpen}
          onClose={onRemoveClose}
          onSubmit={onRemoveSubmit}
          selectedEntityCount={entityCount}
          billingGroupName={selectedBillingGroupName}
        />
      )}
      {isBulkActionsEnabled && (
        <MarkAsStandaloneModal
          isOpen={isStandaloneModalOpen}
          onClose={onStandaloneClose}
          onSubmit={onStandaloneSubmit}
          selectedEntityCount={entityCount}
        />
      )}
      {isBulkActionsEnabled && (
        <>
          <RenameBillingGroupModal
            isOpen={isRenameModalOpen}
            onClose={onRenameClose}
            onSubmit={onRenameSubmit}
            currentBillingGroupName={selectedBillingGroupName}
            selectedGroupCount={groupCount}
          />
          <RemoveBillingGroupModal
            isOpen={isRemoveGroupModalOpen}
            onClose={onRemoveGroupClose}
            onSubmit={onRemoveGroupSubmit}
            billingGroupName={selectedBillingGroupName}
            selectedGroupCount={groupCount}
          />
        </>
      )}
      {isFCCEnabled && (
        <EditFirstCapitalCallDateModal
          isOpen={isEditCapitalCallDateModalOpen}
          onClose={onEditCapitalCallDateClose}
          onSubmit={onEditCapitalCallDateSubmit}
          selectedEntityCount={entityCount}
        />
      )}
      {isEumOverrideEnabled && (
        <>
          <OverrideEumValueModal
            isOpen={isOverrideEumValueModalOpen}
            onClose={onOverrideEumValueClose}
            onSubmit={onOverrideEumValueSubmit}
            selectedEntityCount={entityCount}
            currentEumValue={selectedEntityEumValue}
          />
          <EditEumCalculationValuesModal
            isOpen={isEditEumCalculationValuesModalOpen}
            onClose={onEditEumCalculationValuesClose}
            onSubmit={onEditEumCalculationValuesSubmit}
            selectedEntityCount={entityCount}
            currentValues={selectedEntityCalculationValues}
          />
        </>
      )}
    </>
  );
};
